import type { Registry as Services } from '@ember/service';
import { service } from '@ember/service';
import Model, { attr } from '@ember-data/model';
import { tracked } from '@glimmer/tracking';
import { Type } from '@warp-drive/core-types/symbols';
import { tKey } from 'ember-intl';
import type { Ota } from 'frontend-utils/models';
import type { Platform } from 'frontend-utils/utils/constants/constant-values';
import { PLATFORM_TRANSLATION_KEYS } from 'lumen/utils/constants/general-constants';
import type {
  Breakdown,
  Percentages,
  PercentagesWithSubscriptions,
  PercentagesWithTotal,
} from 'lumen/utils/constants/pi-constant-values';
import { PI_BREAKDOWNS } from 'lumen/utils/constants/pi-constant-values';
import { calculateParityAnalyticsValues } from 'lumen/utils/parity-analytics/calculate-parity-analytics-values';
import generateFakeName from 'lumen/utils/parity-analytics/pa-generate-fake-name';

export const SEGMENTATION_KEYS = {
  ALL: 'all',
  VARIANCE: 'variance_bucket',
  ISSUE_TYPE: 'issue_type',
  BRAND: PI_BREAKDOWNS.BRAND_ID,
  CHANNEL: PI_BREAKDOWNS.CHANNEL,
  CHANNEL_TYPE: PI_BREAKDOWNS.CHANNEL_TYPE,
  MARKET: PI_BREAKDOWNS.MARKET_ID,
  PLATFORM: PI_BREAKDOWNS.PLATFORM,
  SOURCE: PI_BREAKDOWNS.SOURCE,
  SUBSCRIPTION: PI_BREAKDOWNS.SUBSCRIPTION_ID,
} as const;

export type SegmentationKey =
  (typeof SEGMENTATION_KEYS)[keyof typeof SEGMENTATION_KEYS];

const CONFIDENTIAL_BREAKDOWNS: SegmentationKey[] = [
  SEGMENTATION_KEYS.BRAND,
  SEGMENTATION_KEYS.MARKET,
  SEGMENTATION_KEYS.SUBSCRIPTION,
] as const;

export const SEGMENTATION_VALUE_TYPES = {
  PARENT: 'parent',
  CHILD: 'child',
  OTHER: 'other',
} as const;

export type SegmentationItemLike = Partial<
  Omit<ParityInsightSegmentationItem, keyof Model>
>;

export type SegmentationValueType =
  (typeof SEGMENTATION_VALUE_TYPES)[keyof typeof SEGMENTATION_VALUE_TYPES];

export type BreakdownItem = ParityInsightSegmentationItem & {
  segmentationId: Breakdown;
};

export const SOURCES = {
  DIRECTLY_SHOPPED: 'direct',
  TRIVAGO: 'trivago',
  TRIPADVISOR: 'tripadvisor',
  GOOGLEHOTELFINDER: 'googlehotelfinder',
} as const;
export type Source = (typeof SOURCES)[keyof typeof SOURCES];

export const CHANNEL_TYPES = {
  MAJOR: 'contract',
  OTHER: 'non-contract',
} as const;
type ChannelType = (typeof CHANNEL_TYPES)[keyof typeof CHANNEL_TYPES];

const CHANNEL_TYPE_TRANSLATION_KEYS: Record<ChannelType, string> = {
  [CHANNEL_TYPES.MAJOR]: tKey('parity_analytics.contract'),
  [CHANNEL_TYPES.OTHER]: tKey('parity_analytics.non-contract'),
} as const;

export default class ParityInsightSegmentationItem extends Model {
  [Type] = 'parity/insight/segmentation-item' as const;

  @attr declare benchmark?: Percentages;
  @attr declare extractDate: string;
  @attr declare meta?: Record<string, string>;
  @attr declare parentId?: string;
  @attr declare segmentationId: SegmentationKey;
  @attr declare segmentationValue: string;
  @attr declare segmentationValueId: string;
  @attr declare segmentationValueType: SegmentationValueType;
  @attr declare unfilteredId?: string;
  @attr declare value: PercentagesWithSubscriptions;

  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service declare intl: Services['intl'];

  @tracked children?: ParityInsightSegmentationItem[];
  @tracked hasNoData?: boolean;
  @tracked isExpanded?: boolean;
  @tracked isSelectable?: boolean;
  @tracked overridingTotal?: number;
  @tracked unfilteredData?: SegmentationItemLike;

  get calculatedValues(): PercentagesWithTotal {
    return calculateParityAnalyticsValues(this.value, this.overridingTotal);
  }

  get formattedSegmentationValue(): string {
    if (
      this.hotelAndMarketService.isDemoModeActive &&
      CONFIDENTIAL_BREAKDOWNS.includes(this.segmentationId)
    ) {
      return generateFakeName(
        this.segmentationValueId,
        this.segmentationId as Breakdown,
      );
    }

    if (this.segmentationId === PI_BREAKDOWNS.SOURCE) {
      if (this.segmentationValue === SOURCES.DIRECTLY_SHOPPED) {
        return this.intl.t(tKey('parity_analytics.directly_shopped'));
      }
    }

    if (this.segmentationId === PI_BREAKDOWNS.CHANNEL_TYPE) {
      return this.intl.t(
        CHANNEL_TYPE_TRANSLATION_KEYS[
          this.segmentationValue as keyof typeof CHANNEL_TYPE_TRANSLATION_KEYS
        ],
      );
    }

    if (this.segmentationId === PI_BREAKDOWNS.PLATFORM) {
      const platformId = parseInt(this.segmentationValue, 10) as Platform;
      return this.intl.t(PLATFORM_TRANSLATION_KEYS[platformId]);
    }

    if (
      this.segmentationId === PI_BREAKDOWNS.SOURCE ||
      this.segmentationId === PI_BREAKDOWNS.CHANNEL
    ) {
      const record = this.store.peekRecord<Ota>('ota', this.segmentationValue);
      return record !== null ? record.name : this.segmentationValue;
    }

    if (
      this.segmentationId === PI_BREAKDOWNS.MARKET_ID &&
      this.segmentationValue?.startsWith('general.myhotels')
    ) {
      return this.intl.t(this.segmentationValue);
    }

    return this.segmentationValue;
  }

  get hasZeroValues(): boolean {
    const { loss, meet, win } = this.calculatedValues;
    return loss + meet + win === 0;
  }
}
